import { useEffect, useMemo, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input } from '@cloudflare/kumo/components/input';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import { DialogRoot, Dialog, DialogTitle, DialogDescription, DialogClose } from '@cloudflare/kumo/components/dialog';
import { MagnifyingGlass, Trash, ShieldStar, ArrowCounterClockwise, IdentificationCard } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { useSession } from '@/lib/authClient';

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  // kiosk = 当日の受付端末専用アカウント (受付画面 /kiosk だけ使える)
  role: 'admin' | 'kiosk' | null;
  emailVerified: boolean;
  createdAt: string | number;
  updatedAt: string | number;
  passkeyCount: number;
}

interface ListResponse {
  data: UserRow[];
  // pagination は API レスポンスでは meta.pagination に入る (shared/apiResponse.ts createSuccessResponse)
  meta?: {
    pagination?: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      limit: number;
    };
  };
}

const PAGE_LIMIT = 50;

export default function UsersPage() {
  const { data: session } = useSession();
  const selfId = session?.user?.id;

  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_LIMIT)), [total]);

  const fetchUsers = async (opts: { page?: number; search?: string } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(opts.page ?? page));
      params.set('limit', String(PAGE_LIMIT));
      if (opts.search ?? search) params.set('search', opts.search ?? search);
      const res = await api.get<ListResponse>(`/api/admin/users?${params.toString()}`);
      setUsers(res.data.data ?? []);
      setTotal(res.data.meta?.pagination?.totalItems ?? res.data.data?.length ?? 0);
    } catch (err) {
      console.error(err);
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers({ page, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
    fetchUsers({ page: 1, search: searchInput.trim() });
  };

  const setRole = async (user: UserRow, next: UserRow['role']) => {
    setBusyId(user.id);
    setError(null);
    try {
      await api.patch(`/api/admin/users/${user.id}/role`, { role: next });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: next } : u)));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        'role の更新に失敗しました';
      setError(message);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/api/admin/users/${deleteTarget.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setTotal((t) => Math.max(0, t - 1));
      setDeleteTarget(null);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        'ユーザーの削除に失敗しました';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <h1 className="text-2xl font-semibold text-kumo-default">ユーザー管理</h1>
        <Button
          variant="ghost"
          icon={ArrowCounterClockwise}
          loading={loading}
          onClick={() => fetchUsers({ page, search })}
        >
          再読込
        </Button>
      </div>

      <Surface className="p-4">
        <form onSubmit={handleSearch} className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="メールアドレスで検索">
              <Input
                placeholder="例: admin@example.com"
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
              />
            </Field>
          </div>
          <Button type="submit" variant="primary" icon={MagnifyingGlass}>
            検索
          </Button>
        </form>
      </Surface>

      {error ? <Banner variant="error">{error}</Banner> : null}

      <Surface>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : users.length === 0 ? (
          <Empty title="ユーザーが見つかりません" description={search ? `"${search}" に一致するユーザーがいません` : 'まだ登録ユーザーがいません'} />
        ) : (
          <Table layout="auto">
            <Table.Header>
              <Table.Row>
                <Table.Head>メール</Table.Head>
                <Table.Head>役割</Table.Head>
                <Table.Head>パスキー</Table.Head>
                <Table.Head>登録</Table.Head>
                <Table.Head className="text-right">操作</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users.map((user) => {
                const isSelf = user.id === selfId;
                const busy = busyId === user.id;
                return (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <div className="flex flex-col">
                        <span className="text-sm text-kumo-default">{user.email}</span>
                        {user.name ? <span className="text-xs text-kumo-muted">{user.name}</span> : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {user.role === 'admin' ? (
                        <Badge variant="primary">admin</Badge>
                      ) : user.role === 'kiosk' ? (
                        <Badge variant="secondary">受付端末</Badge>
                      ) : (
                        <Badge variant="outline">一般</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-kumo-default">{user.passkeyCount}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-xs text-kumo-muted">{formatDate(user.createdAt)}</span>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex justify-end gap-2">
                        {user.role !== 'kiosk' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={ShieldStar}
                            loading={busy}
                            disabled={isSelf && user.role === 'admin'}
                            title={isSelf && user.role === 'admin' ? '自分の admin 権限は外せません' : undefined}
                            onClick={() => setRole(user, user.role === 'admin' ? null : 'admin')}
                          >
                            {user.role === 'admin' ? '一般に降格' : 'admin に昇格'}
                          </Button>
                        )}
                        {user.role !== 'admin' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={IdentificationCard}
                            loading={busy}
                            title="当日の受付端末専用のアカウントにします (受付画面だけ使えます)"
                            onClick={() => setRole(user, user.role === 'kiosk' ? null : 'kiosk')}
                          >
                            {user.role === 'kiosk' ? '受付端末を解除' : '受付端末にする'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          icon={Trash}
                          disabled={isSelf}
                          title={isSelf ? '自分自身は削除できません' : undefined}
                          onClick={() => setDeleteTarget(user)}
                        >
                          削除
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <div className="flex items-center justify-between">
        <span className="text-xs text-kumo-muted">
          {total} 件中 {users.length === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1}–{(page - 1) * PAGE_LIMIT + users.length} を表示
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            前へ
          </Button>
          <span className="self-center text-xs text-kumo-muted">
            {page} / {totalPages}
          </span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            次へ
          </Button>
        </div>
      </div>

      <DialogRoot
        role="alertdialog"
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>ユーザーを削除しますか?</DialogTitle>
          <DialogDescription>
            <strong>{deleteTarget?.email}</strong> を削除します。 紐付くパスキーと session も同時に削除され、戻せません。
            出展者レコードと紐付いている場合は API 側で拒否されます。
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose
              render={(p) => (
                <Button {...p} variant="secondary">
                  キャンセル
                </Button>
              )}
            />
            <Button variant="destructive" loading={deleting} onClick={confirmDelete}>
              削除する
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function formatDate(value: string | number): string {
  const d = new Date(typeof value === 'number' ? value : value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}
